import { ISortContentFunction } from 'sharebase/filters/sortContent';
import {
	pxItemTypeGuards as typeGuard,
	ShareBaseDocument,
	ShareBaseFolder
} from 'phoenix/domain';
import {
	Menu,
	ValidatorPriority,
	TrashItem
} from 'sharebase/domain';
import { 
	ActionService, 
	ProgressService 
} from 'phoenix/services';
import { ContextMenuService } from 'sharebase/services/contextMenuService';
import { ToastService } from 'sharebase/services/toastService';
import { DeletedItemsDataStore } from '../../dataStores/deletedItemsDataStore';
import { 
	ToolbarService, 
	SortArgs  
} from '../../services/appToolBarService';
import { TrashAction } from './deletedItems.actions';
import { AppAction } from '../../components/_app/app.actions';
import { SelectionDataStore } from '../../dataStores/selectionDataStore';
import { ActionRepository } from 'sharebase/services/actionRepository';
import { AclDatastore } from 'sharebase/dataStores';
import { SelectableItem } from 'sharebase/components';

require('./deletedItems.styles');
const template = require('./deletedItems.template');

/**
 * @ngdoc directive
 * @name sharebase-app.directive:sbaTrashView
 * @module sharebase-app
 * @restrict E
 * @description
 *
 * `sbaTrashView` displays deleted content of a user
 *
 * @usage
 *
 * <sba-trash-view />
 */

	

export function DeletedItemsDirective(): ng.IDirective {
	return {
		restrict: 'E',
		controller: DeletedItemsController,
		controllerAs: 'vm',
		template,
		bindToController: true,
		scope: {},
		link: function (scope: any, element, attrs) {

			scope.$on('$destroy', function(){
				scope.vm.removeSubscriptions();
			});
		}
	};
}

type Trashable = ShareBaseDocument | ShareBaseFolder;
type ConfirmOpts = {
	message: string;
	title: string;
	ok?: string;
	cancel?: string;
	event?: ng.IAngularEvent;
};


export class DeletedItemsController {
	public static $inject = [
		'$state',
		'$mdDialog',
		'sbSortContentFilter',
		'sbaDeletedItemsDataStore',
		'sbActionRepository',
		'sbaSelectionDataStore',
		'sbAclDatastore',
		'sbaToolbarService',
		'pxProgressService',
		'sbContextMenuService',
		'pxActionService',
		'sbToastService',
		'SBA_GET_REQUEST_RIGHTS_ENABLED',
		'gettextCatalog'
	];

	public get selectionMode(): boolean {
		return this.sbaSelectionDataStore.selectedItems.length > 0;
	}

	private loading = false;
	private initialIndex = 0;
	private items:Trashable[] = [];
	private selectedItem:TrashItem;
	private selectedItems:TrashItem[];
	private unsubs:(() => void)[] = [];
	private registeredViews =[
		{name:'list',icon:'list',label: this.gettextCatalog.getString('List'),remember:true},	
		{name:'card',icon:'view_module',label: this.gettextCatalog.getString('Tile'),remember:true}
	];

	// Used to hide all actions except for those in this list.
	private readonly actionWhitelist:string[] = [
		TrashAction.Purge,
		TrashAction.Restore,
		TrashAction.RestoreAll,
		TrashAction.PurgeAll
	];

	constructor(
		private readonly $state,
		private readonly $mdDialog,
		private readonly sortContent:ISortContentFunction,
		private readonly sbaDeletedItemsDS:DeletedItemsDataStore,
		private readonly sbActionRepository:ActionRepository,
		private readonly sbaSelectionDataStore:SelectionDataStore,
		private readonly sbAclDatastore:AclDatastore,
		private readonly sbaToolbarService:ToolbarService,
		private readonly pxProgressService:ProgressService,
		private readonly sbContextMenuService:ContextMenuService,
		private readonly pxActionService:ActionService,
		private readonly sbToastService:ToastService,
		private readonly SBA_GET_REQUEST_RIGHTS_ENABLED:() => boolean,
		private readonly gettextCatalog:ng.gettext.gettextCatalog
	) {
		this.sbaToolbarService.title = this.gettextCatalog.getString('Deleted Items');
		this.hideGlobalActions();
		this.registerMenuItems();
		this.setTrashData();

		// manage subscriptions
		this.unsubs.push(...this.pxActionService.subscribe<any>([
			[TrashAction.Restore, (item) => this.restoreItem(item)],
			[TrashAction.Purge, (item, event) => this.purgeItem(item, event)],
			[TrashAction.PurgeAll, (items, event) => this.purgeAll(items, event)],
			[TrashAction.RestoreAll, (items, event) => this.restoreAll(items)],
			[TrashAction.PurgeSelected, (items, event) => this.purgeAll(items, event)],
			[TrashAction.RestoreSelected, (items, event) => this.restoreAll(items)],
			[AppAction.SortChange, (sortType) => this.sort(sortType)]
		]));

		//add the default folder view types
		this.registeredViews.forEach(element => {
			sbaToolbarService.addView(element);	
		});	
	}

	/**
	 * Loads trash from API
	 */
	public setTrashData(): ng.IPromise<void> {
		this.isLoading();
		return this.sbaDeletedItemsDS
			.load()
			.then(trash => {
				this.sbaToolbarService.actionTarget = trash;
				this.items = trash.map(t => t.item);
				this.sort(this.sbaToolbarService.sortType);
			})
			.finally(() => this.doneLoading());
	}

	/**
	 * Adds the item to the set of selected items via @see sbaSelectionDataStore.
	 *
	 * @param item The item to mark as selected
	 */
	public setItemSelect(item:SelectableItem) {
		this.sbaSelectionDataStore.setItemSelect(item);
	}

	/**
	 * Toggles the selected property for the item.
	 *
	 * @param item The item to toggle
	 */
	public toggleSelection(item:SelectableItem) {		
		if (this.selectionMode) {
			item.selected = !item.selected;
			this.sbaSelectionDataStore.setItemSelect(item);
		}
	}

	/**
	 * Initiates the restore flow for the specified trash item.
	 *
	 * @param item The item being restored.
	 */
	public restoreItem(item:Trashable) {
		const trash = this.sbaDeletedItemsDS.getTrash(item);
		return this.sbaDeletedItemsDS.restoreItem(trash)
			.then(() => {
				const state = this.$state;
				const message  = this.gettextCatalog.getString('{{itemName}} has been restored', { 
					itemName: trash.itemName 
				});
		
				let itemState:string;
				let itemStateParam;
				let item = trash.item;
				
				if (typeGuard.isShareBaseDocument(item)) {					
					itemState = 'content.myFiles.document';
					itemStateParam = { 
						docID: item.DocumentId
					};
				} else {
					itemState = 'content.myFiles.folder';
					itemStateParam = { 
						folderID: item.FolderId 
					};
				}
				
				this.sbToastService.showSimpleAction(message, this.gettextCatalog.getString('Open'), () =>  state.go(itemState,itemStateParam), 'open_in_browser');
				this.sbaSelectionDataStore.clearSelection();
				this.removeItem(item);
			})
			.catch((res) => {
				let message;
				if (typeGuard.isShareBaseDocument(item)) {
					message = this.gettextCatalog.getString('Unable to restore the selected document');
				} else {
					if (res.status === 409) {
						message = this.gettextCatalog.getString('Unable to restore the folder. Another folder with this name already exists');
					} else {
						message = this.gettextCatalog.getString('Unable to restore the selected folder');
					}
				}

				this.sbToastService.showSimple(message);
			});
	}

	public restoreAll(items:TrashItem[]) {
		this.isLoading();
		return this.sbaDeletedItemsDS.restoreAll(items)
			.then(() => {
				let message = '';
				if (items.length === this.sbaDeletedItemsDS.deletedItems.length) {
					message = this.gettextCatalog.getString('All items have been restored');
				} else {
					message = this.gettextCatalog.getString('The selected items have been restored');
				}
				this.sbToastService.showSimple(message);
				this.items = [];
			})
			.catch(() => {
				let message = this.gettextCatalog.getString('Unable to restore all items');
				this.sbToastService.showSimple(message);
			})
			.finally(() => {
				this.sbaSelectionDataStore.clearSelection();
				this.doneLoading();
			});
	}

	/**
	 * Initiates the purge flow for the specified trash item
	 *
	 * @param item The item to purge
	 */
	public purgeItem(item:Trashable, event?:ng.IAngularEvent) {
		const trash = this.sbaDeletedItemsDS.getTrash(item);
		let title = this.gettextCatalog.getString('Purge item?');
		let confirmMsg = this.gettextCatalog.getString('The item will be permanently deleted.');
		let opts: ConfirmOpts = { 
			message: confirmMsg, 
			title,
			ok: this.gettextCatalog.getString('Purge'),
			cancel: this.gettextCatalog.getString('Cancel') 
		};
		if (event) {
			opts.event = event;
		}

		return this.confirm(opts)
			.then(() => this.sbaDeletedItemsDS.purgeItem(trash))
			.then(() => {
				this.sbaToolbarService.actionTarget = this.sbaDeletedItemsDS.deletedItems;
				let message = this.gettextCatalog.getString('{{name}} has been deleted permanently', { name: trash.itemName });
				this.sbToastService.showSimple(message);
				this.removeItem(item);
			})
			.catch((res) => {
				// The dialog will reject its promise with no value, don't display
				// the error messages below if this is the case. So make sure "res" is
				// some value, because if it isn't, we can assume the user cancelled.
				if (res) {
					let message = '';
					if (res.status === 409 && res.data.StatusCode === 'ERR_FOLDER_ALREADY_EXISTS') {
						message = this.gettextCatalog.getString('A folder with that name already exists');
					}
					else if (typeGuard.isShareBaseDocument(item)) {
						message = this.gettextCatalog.getString('Unable to restore the selected document');
					}
					else {
						message = this.gettextCatalog.getString('Unable to restore the selected folder');
					}
					this.sbToastService.showSimple(message);
								
				}
			})
			.finally(() => {
				this.sbaSelectionDataStore.clearSelection();
			});
	}

	/**
	 * Initiates the purge flow for all items in the trash. If a list of items is provided,
	 * then only those items in the list will be purged.
	 *
	 * @param item An optional list of items to purge.
	 */
	public purgeAll(trash:TrashItem[], event?: ng.IAngularEvent) {
		let title = trash.length !== this.sbaDeletedItemsDS.deletedItems.length ? 
			this.gettextCatalog.getString('Purge selected items?') :
			this.gettextCatalog.getString('Purge all items?');
		let confirmMessage = this.gettextCatalog.getString('These items will be permanently deleted.');

		let opts: ConfirmOpts = {
			message: confirmMessage,
			title,
			ok: this.gettextCatalog.getString('Purge'),
			cancel: this.gettextCatalog.getString('Cancel')
		};
		if (event) {
			opts.event = event;
		}

		return this.confirm(opts)
			.then(() => {
				return this.sbaDeletedItemsDS.purgeAll(trash);
				this.items = [];
			})
			.then(() => {
				let message = this.gettextCatalog.getString('The selected items have been permanently deleted.');
				this.sbToastService.showSimple(message);
			})
			.catch((res) => {
				// only show message if "res" has a truthy value, otherwise assume the user cancelled
				if (res) {
					let message = this.gettextCatalog.getString('Unable to purge all selected items');
					this.sbToastService.showSimple(message);							
				}
			})
			.finally(() => {
				this.sbaSelectionDataStore.clearSelection();
			})
	}

	/**
	 * Event handler on the virtual tile controller to display the context menu.
	 *
	 * @param {Object} payload The target item on which to display the menu.
	 */
	public showVirtualContextMenu(payload: { item:Trashable, event:MouseEvent}) {
		if(payload.item && payload.event){
			this.showContextMenu(payload.item, payload.event);
		}
	}

	/**
	 * Display the context menu on the provided item.
	 *
	 * @param trash Target item for the context menu action.
	 * @param event Event that triggered displaying the menu.
	 */
	public showContextMenu(item:ShareBaseDocument|ShareBaseFolder, event:MouseEvent) {
		this.selectedItem = this.sbaSelectionDataStore.selectedItems.length === 1 ? this.sbaSelectionDataStore.selectedItems[0] : item;
		
		// load acl upfront to minimize duplicate permission request, then show context menu via service call
		this.sbAclDatastore
			.load(item)
			.then(() => this.sbContextMenuService.open(item, event));
	}

	/**
	 * Cleans up all context menu and action service subscriptions
	 * created by this component.
	 */
	public removeSubscriptions() {
		this.unsubs.forEach(unsub => unsub());
	}

	/**
	 * Convenience method for displaying a confirmation dialog.
	 *
	 * @param opts Confirmation message options.
	 * @returns A promise that will be resolved if the user confirms
	 * or rejects if the user cancels.
	 */
	private confirm(opts: ConfirmOpts): ng.IPromise<any> {
		let confirm = this.$mdDialog.confirm()
			.textContent(opts.message)
			.title(opts.title)
			.ok(opts.ok || this.gettextCatalog.getString('Yes'))
			.cancel(opts.cancel || this.gettextCatalog.getString('No'));

		if (opts.event) {
			confirm.targetEvent(opts.event);
		}

		return this.$mdDialog.show(confirm);
	}

	/**
	 * Hides global context menu actions until the component is destroyed.
	 */
	private hideGlobalActions(): void {
		let menus:Menu[] = [
			this.sbContextMenuService,
			this.sbaToolbarService,
			this.sbaSelectionDataStore.toolbar
		];

		menus.forEach(menu => {
			Object.keys(menu.items)
				.forEach(k => {
					this.unsubs.push(menu.items[k].addValidator(() => false, ValidatorPriority.High));
				})
		});
	}

	/**
	 * Registers context menu actions specific to this component. These
	 * actions will be removed when the component is destroyed.
	 */
	private registerMenuItems(): void {
		const selectionBar = this.sbaSelectionDataStore.toolbar;
		const multiSelectValidator = (target:TrashItem, targets:TrashItem[]) => targets.length > 1;

		// single-item actions
		this.sbActionRepository
			.getActions([
				TrashAction.Purge,
				TrashAction.Restore
			])
			.map(a => this.sbActionRepository.createFromAction(a, !this.SBA_GET_REQUEST_RIGHTS_ENABLED()))
			.forEach(item => {
				this.unsubs.push(
					this.sbContextMenuService.addItem(item),
					selectionBar.copyFromMenu(item.action, this.sbContextMenuService)
				);
				item = selectionBar.getItem(item.action);
			});		
			

		// multi-item actions
		this.sbActionRepository
			.getActions([
				TrashAction.PurgeSelected,
				TrashAction.RestoreSelected
			])
			.map(a => this.sbActionRepository.createFromAction(a, !this.SBA_GET_REQUEST_RIGHTS_ENABLED()))
			.forEach(item => {
				this.unsubs.push(selectionBar.addItem(item));			
				selectionBar.getItem(item.action).addValidator(multiSelectValidator);
			});

		// toolbar items
		this.sbActionRepository
			.getActions([
				TrashAction.RestoreAll,
				TrashAction.PurgeAll
			])
			.map(a => this.sbActionRepository.createFromAction(a, false))
			.forEach(item => {
				this.unsubs.push(this.sbaToolbarService.addItem(item));
				item.addValidator((t, all) =>  all.length > 0);
			});
	}

	/**
	 * Sets appropriate properties so UI can display a loading icon.
	 */
	private isLoading(): void {
		this.loading = true;
		this.pxProgressService.visible = true;
	}

	/**
	 * Sets appropriate properties so UI can hide the loading icon.
	 */
	private doneLoading(): void {
		this.loading = false;
		this.pxProgressService.visible = false;
	}

	private sort(sortType:SortArgs) {
		this.items = this.sortContent(this.items, sortType.type, sortType.ascending) as Trashable[];
	}

	private removeItem(item:Trashable){
		let removeIndex = this.items.indexOf(item);
		this.items.splice(removeIndex, 1);
	}
}


// WEBPACK FOOTER //
// ./src/sharebase-app/components/deletedItems/deletedItems.ts